import { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import moment from 'moment'

import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import { People } from '@mui/icons-material'
import {
  Box,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
  IconButton
} from '@mui/material'

import { constants as cons } from '../../Constants/constants'
import { BOOLEAN_OPTIONS } from '../../Constants/type'
import { GlobalDialog } from '../../Component/GlobalDialog'
import { TempAutoSelectInput } from '../../Component/Input/TempAutoSelectInput'
import { TempBtn } from '../../Component/Input/TempBtn'
import { TempCheckBox } from '../../Component/Input/TempCheckBox'
import { TempTextInput } from '../../Component/Input/TempTextInput'
import MUITable from '../../Component/common/table/MUITable'
import { ListFilter } from '../../Component/ListDisplay/ListFilter'

export const AUTO_TASK_DEFAULT_ITEM = {
  id: null,
  taskType: null,
  mobile: true,
  kiosk: true,
  executionType: null,
  executionExactDateTime: null,
  executionResetTime: null,
  executionOffsetMinute: null,
  status: 'A',
}

const TASK_TYPE_OPTIONS = [
  { value: 'REQUEST_TICKET_PENDING', name: 'REQUEST_TICKET_PENDING' },
  { value: 'REQUEST_TICKET_ACTIVE', name: 'REQUEST_TICKET_ACTIVE' },
]

const EXECUTION_TYPE_OPTIONS = [
  { value: 'EXACT_DATE_TIME', name: 'EXACT_DATE_TIME' },
  { value: 'RESET_OFFSET_MINUTE', name: 'RESET_OFFSET_MINUTE' },
  { value: 'RESET_TIME', name: 'RESET_TIME' },
]

const DEFAULT_BATCH_LIST_FILTERS = [
  {
    className: 'filter-input',
    label: { resource: 'QueueTemplate', key: 'taskType' },
    value: '',
    type: cons.TYPE.SELECT,
    selectValue: TASK_TYPE_OPTIONS,
    name: 'taskType',
    size: cons.SIZE.SMALL,
  },
  {
    className: 'filter-input',
    label: { resource: 'QueueTemplate', key: 'executionType' },
    value: '',
    type: cons.TYPE.SELECT,
    selectValue: EXECUTION_TYPE_OPTIONS,
    name: 'executionType',
    size: cons.SIZE.SMALL,
  },
  {
    className: 'filter-input',
    label: { resource: 'QueueTemplate', key: 'value' },
    value: '',
    name: 'value',
    size: cons.SIZE.SMALL,
  },
]

export const QueueTemplateAutoTaskDialog = ({
  open = false,
  resource = 'TimeSection',
  tableTypeOptions = [],
  initialAutoTaskList = [],
  existingAutoTaskRows = [],
  onClose,
  onApply,
}) => {
  const [autoTaskList, setAutoTaskList] = useState([])
  const [applyAllTableTypes, setApplyAllTableTypes] = useState(true)
  const [selectedTableTypeIds, setSelectedTableTypeIds] = useState([])
  const [activeTab, setActiveTab] = useState(0)
  const [batchAction, setBatchAction] = useState('DELETE_SELECTED')
  const [taskFilterKeyword, setTaskFilterKeyword] = useState('')
  const [selectedExistingRows, setSelectedExistingRows] = useState([])
  const [batchReplaceAutoTaskList, setBatchReplaceAutoTaskList] = useState([_.cloneDeep(AUTO_TASK_DEFAULT_ITEM)])
  const [batchPagination, setBatchPagination] = useState({ page: 0, pageSize: 10 })
  const [error, setError] = useState(null)
  const [listFilters, setListFilters] = useState(_.cloneDeep(DEFAULT_BATCH_LIST_FILTERS))

  useEffect(() => {
    if (!open) {
      return
    }

    const nextTableTypeOptions = Array.isArray(tableTypeOptions) ? tableTypeOptions : []
    const nextAutoTaskList =
      Array.isArray(initialAutoTaskList) && initialAutoTaskList.length > 0
        ? _.cloneDeep(initialAutoTaskList)
        : []

    setAutoTaskList(nextAutoTaskList)
    setApplyAllTableTypes(true)
    setSelectedTableTypeIds(nextTableTypeOptions.map((tableType) => tableType.id))
    setActiveTab(0)
    setBatchAction('DELETE_SELECTED')
    setTaskFilterKeyword('')
    setSelectedExistingRows([])
    setBatchReplaceAutoTaskList([_.cloneDeep(AUTO_TASK_DEFAULT_ITEM)])
    setBatchPagination({ page: 0, pageSize: 10 })
    setListFilters(_.cloneDeep(DEFAULT_BATCH_LIST_FILTERS))
  }, [open, tableTypeOptions, initialAutoTaskList])

  const selectedTableTypeIdSet = useMemo(() => new Set(selectedTableTypeIds || []), [selectedTableTypeIds])

  const hasExistingAutoTaskRows = Array.isArray(existingAutoTaskRows) && existingAutoTaskRows.length > 0

  const filteredExistingAutoTaskRows = useMemo(() => {
    const sourceRows = existingAutoTaskRows || []
    const listFilterMap = (listFilters || []).reduce((result, filter) => {
      result[filter.name] = filter.value
      return result
    }, {})

    const filterTaskType = String(listFilterMap.taskType || '').trim()
    const filterExecutionType = String(listFilterMap.executionType || '').trim()
    const filterValue = String(listFilterMap.value || '').trim().toLowerCase()
    const keyword = String(taskFilterKeyword || '').trim().toLowerCase()

    const isNoFilter = !filterTaskType && !filterExecutionType && !filterValue && !keyword
    if (isNoFilter) {
      return sourceRows
    }

    return sourceRows.filter((row) => {
      const rowTaskType = String(row?.taskType || '')
      const rowExecutionType = String(row?.executionType || '')
      const rowValue = String(row?.value || '').toLowerCase()

      const searchableText = [
        row?.label,
        row?.tableTypeLabel,
        row?.taskType,
        row?.executionType,
        row?.status,
        row?.backgroundColor,
        row?.foregroundColor,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      if (filterTaskType && rowTaskType !== filterTaskType) {
        return false
      }

      if (filterExecutionType && rowExecutionType !== filterExecutionType) {
        return false
      }

      if (filterValue && !rowValue.includes(filterValue)) {
        return false
      }

      if (keyword && !searchableText.includes(keyword)) {
        return false
      }

      return true
    })
  }, [existingAutoTaskRows, listFilters, taskFilterKeyword])

  const onChangeListFilters = (nextListFilters) => {
    setListFilters(_.cloneDeep(nextListFilters || []))
    setBatchPagination((previousState) => ({
      ...previousState,
      page: 0,
    }))
    setSelectedExistingRows([])
  }

  const onChangeListFiltersImmediate = (nextListFilters) => {
    setListFilters(_.cloneDeep(nextListFilters || []))
    setBatchPagination((previousState) => ({
      ...previousState,
      page: 0,
    }))
    setSelectedExistingRows([])
  }

  const normalizedExistingAutoTaskRows = useMemo(
    () =>
      (filteredExistingAutoTaskRows || []).map((row) => ({
        ...row,
        id: row.rowId,
      })),
    [filteredExistingAutoTaskRows]
  )

  const paginatedExistingRows = useMemo(() => {
    const offset = batchPagination.page * batchPagination.pageSize
    return normalizedExistingAutoTaskRows.slice(offset, offset + batchPagination.pageSize)
  }, [normalizedExistingAutoTaskRows, batchPagination.page, batchPagination.pageSize])

  const selectedExistingRowIdSet = useMemo(() => new Set((selectedExistingRows || []).map((row) => row.id)), [selectedExistingRows])

  const onChangeTaskListInput = (name, value, listStateSetter) => {
    listStateSetter((previousState) => {
      const nextTaskList = _.cloneDeep(previousState)
      const matched = String(name).match(/^(autoTaskList|replaceAutoTaskList)\.(\d+)\.(.+)$/)

      if (!matched) {
        return previousState
      }

      const index = Number(matched[2])
      const field = matched[3]

      if (field === 'executionExactDateTime') {
        _.set(nextTaskList, [index, field], value ? moment(value).valueOf() : null)
      } else if (field === 'executionOffsetMinute') {
        _.set(nextTaskList, [index, field], value === '' || value === null || value === undefined ? null : Number(value))
      } else {
        _.set(nextTaskList, [index, field], value === '' ? null : value)
      }

      return nextTaskList
    })
  }

  const onChangeAutoTaskDialogInput = (name, value) => {
    onChangeTaskListInput(name, value, setAutoTaskList)
  }

  const onChangeReplaceAutoTaskInput = (name, value) => {
    onChangeTaskListInput(name, value, setBatchReplaceAutoTaskList)
  }

  const onAddAutoTaskItem = () => {
    setAutoTaskList((previousState) => [...previousState, _.cloneDeep(AUTO_TASK_DEFAULT_ITEM)])
  }

  const onRemoveAutoTaskItem = (index) => {
    setAutoTaskList((previousState) => {
      const nextAutoTaskList = previousState.filter((_, currentIndex) => currentIndex !== index)
      return nextAutoTaskList.length > 0 ? nextAutoTaskList : [_.cloneDeep(AUTO_TASK_DEFAULT_ITEM)]
    })
  }

  const onAddReplaceAutoTaskItem = () => {
    setBatchReplaceAutoTaskList((previousState) => [...previousState, _.cloneDeep(AUTO_TASK_DEFAULT_ITEM)])
  }

  const onRemoveReplaceAutoTaskItem = (index) => {
    setBatchReplaceAutoTaskList((previousState) => {
      const nextAutoTaskList = previousState.filter((_, currentIndex) => currentIndex !== index)
      return nextAutoTaskList.length > 0 ? nextAutoTaskList : [_.cloneDeep(AUTO_TASK_DEFAULT_ITEM)]
    })
  }

  const onChangeAutoTaskApplyAllTableTypes = (checked) => {
    setApplyAllTableTypes(checked)

    if (checked) {
      setSelectedTableTypeIds((tableTypeOptions || []).map((tableType) => tableType.id))
    }
  }

  const onChangeAutoTaskTableTypeSelection = (tableTypeId, checked) => {
    setSelectedTableTypeIds((previousState) => {
      const nextSelectedSet = new Set(previousState || [])

      if (checked) {
        nextSelectedSet.add(tableTypeId)
      } else {
        nextSelectedSet.delete(tableTypeId)
      }

      const nextSelectedTableTypeIds = Array.from(nextSelectedSet)
      const totalTableTypeCount = (tableTypeOptions || []).length

      setApplyAllTableTypes(totalTableTypeCount > 0 && nextSelectedTableTypeIds.length === totalTableTypeCount)

      return nextSelectedTableTypeIds
    })
  }

  const existingTaskColumns = useMemo(
    () => [
      {
        field: 'label',
        headerName: 'Table Type',
        width: 100,
        renderCell: ({ row }) => tableTypeLabel(row),
      },
      { field: 'taskType', headerName: 'Task Type', width: 180, renderCell: ({ value }) => value || '-' },
      { field: 'executionType', headerName: 'Execution Type', width: 180, renderCell: ({ value }) => value || '-' },
      {
        field: 'value',
        headerName: 'Value',
        width: 220,
        renderCell: ({ row }) =>
          row.executionType === 'EXACT_DATE_TIME' ? (row.value ? moment(row.value).format('YYYY-MM-DDTHH:mm:ss') : '-') : row.value || '-',
      },
      { field: 'status', headerName: 'Status', width: 120, renderCell: ({ value }) => value || '-' },
    ],
    []
  )

  const handleApply = () => {


    if (onApply) {
      const selectedExistingTaskRefs = (existingAutoTaskRows || [])
        .filter((row) => selectedExistingRowIdSet.has(row.rowId))
        .map((row) => ({
          tableTypeId: row.tableTypeId,
          taskIndex: row.taskIndex,
          rowId: row.rowId,
        }))

      const operationMode = activeTab === 1 && hasExistingAutoTaskRows ? batchAction : 'APPEND'

      const nextAutoTaskList =
        activeTab === 1 && batchAction === 'REPLACE_SELECTED'
          ? _.cloneDeep(batchReplaceAutoTaskList)
          : _.cloneDeep(autoTaskList)
      
      let bypassKeyList = ['id']
      const isAllThingFilled = nextAutoTaskList.every((autoTask) => {
        switch (autoTask.executionType) {
          case 'EXACT_DATE_TIME':
            bypassKeyList.push('executionResetTime', 'executionOffsetMinute')
            break
          
          case 'RESET_TIME':
            bypassKeyList.push('executionExactDateTime', 'executionOffsetMinute')
            break

          case 'RESET_OFFSET_MINUTE':
            bypassKeyList.push('executionExactDateTime', 'executionResetTime')
            break

          default:
        }

        return Object.entries(autoTask).every(([key, value]) => {
          // console.log('autoTask', key, bypassKeyList, value !== null)
          return bypassKeyList.includes(key) || value !== null
        })
      })

      if (!isAllThingFilled && !(batchAction === 'DELETE_SELECTED' && activeTab == 1)) {
        setError('Please fill in all required fields for each auto task item before applying.')
        return
      }

      onApply({
        autoTaskList: nextAutoTaskList,
        applyAllTableTypes,
        selectedTableTypeIds: _.cloneDeep(selectedTableTypeIds),
        operationMode,
        selectedExistingTaskRefs,
      })
    }
  }

  const tableTypeLabel = (tableTypeOption) => {
    return (
        <div style={{ width: 'fit-content' }}>
            <div style={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
            <div
                style={{
                backgroundColor: tableTypeOption.backgroundColor || 'white',
                color: tableTypeOption.foregroundColor || 'black',
                padding: '5px 10px',
                borderRadius: '5px',
                }}
            >
                {tableTypeOption?.label}
            </div>
            <div className="table-type-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <People fontSize={cons.SIZE.SMALL} />
                {tableTypeOption?.minSize}-{tableTypeOption?.maxSize}
            </div>
            </div>
        </div>
    )
  }

  const renderAutoTaskForm = ({ list, listName, onChange, onAdd, onRemove }) => (
    <>
      {
        onAdd && (
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <TempBtn
              btnSetting={{
                variant: cons.STYLE.VARIANT.CONTAINED,
                color: cons.STYLE.PRIMARY,
                label: { key: 'add' },
                icon: <AddIcon />,
                onClick: onAdd,
              }}
            />
          </Stack>
        )
      }

      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No auto task items yet.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {list.map((autoTask, index) => (
            <Box
              key={autoTask.id || `${listName}-${index}`}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
            >
              {
                onRemove && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">{autoTask.id || `Auto task ${index + 1}`}</Typography>
                    {/* <TempBtn
                      btnSetting={{
                        variant: cons.STYLE.VARIANT.OUTLINED,
                        color: cons.STYLE.ERROR,
                        label: { key: 'remove' },
                        icon: <DeleteIcon />,
                        onClick: () => onRemove(index),
                      }}
                    /> */}
                    <Tooltip title="Remove">
                      <IconButton color="error" onClick={() => onRemove(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )
              }

              <TempAutoSelectInput
                inputSetting={{
                  label: { resource: resource, key: 'taskType' },
                  value: autoTask.taskType,
                  selectValue: TASK_TYPE_OPTIONS,
                  name: `${listName}.${index}.taskType`,
                  disableClearable: false,
                }}
                handleChange={onChange}
              />

              <TempAutoSelectInput
                inputSetting={{
                  name: `${listName}.${index}.mobile`,
                  label: { resource: resource, key: 'mobile' },
                  value: autoTask.mobile,
                  selectValue: BOOLEAN_OPTIONS,
                }}
                handleChange={onChange}
              />

              <TempAutoSelectInput
                inputSetting={{
                  name: `${listName}.${index}.kiosk`,
                  label: { resource: resource, key: 'kiosk' },
                  value: autoTask.kiosk,
                  selectValue: BOOLEAN_OPTIONS,
                }}
                handleChange={onChange}
              />

              <TempAutoSelectInput
                inputSetting={{
                  label: { resource: resource, key: 'executionType' },
                  value: autoTask.executionType,
                  selectValue: EXECUTION_TYPE_OPTIONS,
                  name: `${listName}.${index}.executionType`,
                  disableClearable: false,
                }}
                handleChange={onChange}
              />

              {autoTask.executionType === 'EXACT_DATE_TIME' && (
                <TempTextInput
                  inputSetting={{
                    label: { resource: resource, key: 'executionExactDateTime' },
                    value: autoTask.executionExactDateTime,
                    name: `${listName}.${index}.executionExactDateTime`,
                    type: cons.TYPE.DATETIME_LOCAL,
                    clear: true,
                  }}
                  handleChange={onChange}
                />
              )}

              {autoTask.executionType === 'RESET_TIME' && (
                <TempTextInput
                  inputSetting={{
                    label: { resource: resource, key: 'executionResetTime' },
                    value: autoTask.executionResetTime,
                    name: `${listName}.${index}.executionResetTime`,
                    type: cons.TYPE.TIME,
                    clear: true,
                    step: 1,
                  }}
                  handleChange={onChange}
                />
              )}

              {autoTask.executionType === 'RESET_OFFSET_MINUTE' && (
                <TempTextInput
                  inputSetting={{
                    label: { resource: resource, key: 'executionOffsetMinute' },
                    value: autoTask.executionOffsetMinute,
                    name: `${listName}.${index}.executionOffsetMinute`,
                    type: cons.TYPE.NUMBER,
                    clear: true,
                  }}
                  handleChange={onChange}
                />
              )}

              <TempAutoSelectInput
                inputSetting={{
                  label: { resource: resource, key: 'status' },
                  value: autoTask.status,
                  selectValue: cons.CHOICES.QUEUE_TEMPLATE.STATUS,
                  name: `${listName}.${index}.status`,
                  disableClearable: true,
                }}
                handleChange={onChange}
              />
            </Box>
          ))}
        </Stack>
      )}
    </>
  )

  const dialogInfo = open
    ? {
        open: true,
        onClose: onClose,
        maxWidth: 'lg',
        dialogTitle: { resource, key: 'autoTaskListManagement' },
        dialogContent: (
            <Stack spacing={2} sx={{ minWidth: 860 }}>
            {hasExistingAutoTaskRows && (
                <Tabs value={activeTab} onChange={(_, nextTab) => setActiveTab(nextTab)}>
                    <Tab label="Add / Append" />
                    <Tab label="Batch Manage Existing" />
                </Tabs>
            )}

            {activeTab === 0 && (
                <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                        Choose which table types under this time section should receive the auto-task list.
                    </Typography>
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    spacing={0}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, '& > :nth-of-type(2)': { pl: 2 } }}
                    >
                    <TempCheckBox
                        checkBoxSetting={{
                        value: !!applyAllTableTypes,
                        label: { key: 'all' },
                        }}
                        handleChange={() => onChangeAutoTaskApplyAllTableTypes(!applyAllTableTypes)}
                    />

                    {(tableTypeOptions || []).length > 0 ? (
                        <>
                        {(tableTypeOptions || []).map((tableTypeOption) => {
                            const currentChecked =
                            !!applyAllTableTypes || selectedTableTypeIdSet.has(tableTypeOption.id)

                            return (
                            <Stack direction="row" key={tableTypeOption.id} alignItems="center" spacing={0} sx={{ pr: 2, my: 0.5 }}>
                                <TempCheckBox
                                checkBoxSetting={{
                                    className: 'd-flex',
                                    value: currentChecked,
                                    label: { key: tableTypeOption.label },
                                    customLabel: (
                                        tableTypeLabel(tableTypeOption)
                                    ),
                                }}
                                handleChange={() => onChangeAutoTaskTableTypeSelection(tableTypeOption.id, !currentChecked)}
                                />
                            </Stack>
                            )
                        })}
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                        No table types in this time section yet.
                        </Typography>
                    )}
                </Stack>

                {renderAutoTaskForm({
                  list: autoTaskList,
                  listName: 'autoTaskList',
                  onChange: onChangeAutoTaskDialogInput,
                  onAdd: onAddAutoTaskItem,
                  onRemove: onRemoveAutoTaskItem,
                })}
                </>
            )}

            {activeTab === 1 && hasExistingAutoTaskRows && (
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                    Select existing auto tasks under this time section, then choose delete or replace.
                    </Typography>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    {/* <TextField
                        fullWidth
                        size="small"
                        label="Filter existing tasks"
                        value={taskFilterKeyword}
                        onChange={(event) => {
                          setTaskFilterKeyword(event.target.value)
                          setBatchPagination((previousState) => ({ ...previousState, page: 0 }))
                        }}
                    /> */}
                    <TempAutoSelectInput
                        inputSetting={{
                        label: { key: 'actionType' },
                        value: batchAction,
                        selectValue: [
                            { value: 'DELETE_SELECTED', name: 'Delete Selected' },
                            { value: 'REPLACE_SELECTED', name: 'Replace Selected' },
                        ],
                        name: 'batchAction',
                        disableClearable: true,
                        }}
                        handleChange={(name, value) => setBatchAction(value)}
                    />
                    </Stack>

                    {batchAction === 'REPLACE_SELECTED' && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Define replacement auto tasks for selected existing rows.
                        </Typography>
                        {renderAutoTaskForm({
                          list: batchReplaceAutoTaskList,
                          listName: 'replaceAutoTaskList',
                          onChange: onChangeReplaceAutoTaskInput,
                          // onAdd: onAddReplaceAutoTaskItem,
                          // onRemove: onRemoveReplaceAutoTaskItem,
                        })}
                      </>
                    )}

                    {error && (
                      <Typography variant="body2" color="error">
                        {error}
                      </Typography>
                    )}

                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                      <ListFilter
                        onChangeListFilters={onChangeListFilters}
                        listFilters={listFilters}
                        // handleFilterExtraChange={onChangeListFiltersImmediate}
                      />
                      <MUITable
                        columns={existingTaskColumns}
                        rows={paginatedExistingRows}
                        pagination={batchPagination}
                        setPagination={setBatchPagination}
                        listLength={normalizedExistingAutoTaskRows.length}
                        handleChangePage={(event, page) => setBatchPagination((previousState) => ({ ...previousState, page }))}
                        handleChangeRowsPerPage={(event) =>
                          setBatchPagination({ page: 0, pageSize: Number(event?.target?.value || 10) })
                        }
                        selected={selectedExistingRows}
                        setSelected={setSelectedExistingRows}
                      />
                    </Box>
                </Stack>
            )}
          </Stack>
        ),
        dialogActions: [
          { label: { key: 'apply' }, onClick: handleApply, icon: <SaveIcon /> },
          { label: { key: 'cancel' }, onClick: onClose, icon: <ClearIcon /> },
        ],
      }
    : null

  return dialogInfo ? <GlobalDialog dialogInfo={dialogInfo} /> : null
}
